import {
  type NotificationChannelInfo,
  type NotificationChannelCloseEvent,
  type NotificationChannelErrorEvent,
  type NotificationChannelEventMap,
  type NotificationChannelMessageEvent,
  type NotificationChannel,
  type ChannelInfo,
} from '@goto/shell-notification-channel'
import { getShellLogger } from '../../common/logger'
import { getEventBus } from '../namespaces/event-bus'
import { NotificationChannelEvents, NotificationChannelNamespace } from '../notification-channel'
import { NotificationHandlerEventType } from '@goto/shell-notification-channel'

export class ShellNotificationChannelFacade implements NotificationChannel {
  private readonly notificationNamespace = getEventBus().register(
    NotificationChannelNamespace,
    NotificationChannelEvents,
  )

  constructor(private notificationChannel: NotificationChannel) {
    this.addListeners()
  }

  get id(): string {
    return this.notificationChannel.id
  }

  get channelInfo(): NotificationChannelInfo | undefined {
    return this.notificationChannel.channelInfo
  }

  get isConnected(): boolean | undefined {
    return this.notificationChannel.isConnected
  }

  public start(): void {
    this.notificationChannel.start()
  }

  public addListener<K extends keyof NotificationChannelEventMap>(
    type: K,
    listener: (ev: NotificationChannelEventMap[K]) => void,
  ): void {
    this.notificationChannel.addListener(type, listener)
  }

  public removeListener<K extends keyof NotificationChannelEventMap>(
    type: K,
    listener: (ev: NotificationChannelEventMap[K]) => void,
  ): void {
    this.notificationChannel.removeListener(type, listener)
  }

  /**
   * @deprecated
   * Use notification.addListener(type, listener) instead
   */
  public subscribe(listener: (event: NotificationChannelMessageEvent) => void) {
    this.notificationChannel.addListener(NotificationHandlerEventType.MESSAGE, listener)
  }

  /**
   * @deprecated
   * Use notification.removeListener(type, listener) instead
   */
  public unsubscribe(listener: (event: NotificationChannelMessageEvent) => void) {
    this.notificationChannel.removeListener(NotificationHandlerEventType.MESSAGE, listener)
  }

  private addListeners(): void {
    this.notificationChannel.addListener(NotificationHandlerEventType.OPEN, this.handleHandlerOpenEvent)
    this.notificationChannel.addListener(NotificationHandlerEventType.CLOSE, this.handleHandlerCloseEvent)
    this.notificationChannel.addListener(NotificationHandlerEventType.MESSAGE, this.handleHandlerMessageEvent)
    this.notificationChannel.addListener(NotificationHandlerEventType.ERROR, this.handleHandlerErrorEvent)
  }

  private handleHandlerOpenEvent = (): void => {
    const channelInfo: ChannelInfo = {
      channelId: this.channelInfo?.channelId ?? '',
      channelLifetime: this.channelInfo?.channelLifetime ?? 0,
    }
    this.notificationNamespace.emit.connected(channelInfo)
  }

  private handleHandlerCloseEvent = (event: NotificationChannelCloseEvent): void => {
    this.notificationNamespace.emit.disconnected(event as any) // TODO remove `as any` when the old NC will be remove [SCORE-2503]
    getShellLogger().error(event)
  }

  private handleHandlerMessageEvent = (event: NotificationChannelMessageEvent<string>): void => {
    this.notificationNamespace.emit.message(event as any) // TODO remove `as any` when the old NC will be remove [SCORE-2503]
  }

  private handleHandlerErrorEvent = (event: NotificationChannelErrorEvent): void => {
    getShellLogger().error('WebSocket:Error', event)
    this.notificationNamespace.emit.error(event as any) // TODO remove `as any` when the old NC will be remove [SCORE-2503]
  }
}
